import { cn } from '@/utils'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'

import type { SubTimer } from '~shared/rundown/cue'

import { momentAtom } from '@/store/moment.store'
import { runnerAtom } from '@/store/runner.store'

import { CueElapsed } from '@/features/rundown/cue/CueElapsed'
import { useCueOutputSubTimers } from '@/features/rundown/cue/sub-timers/sub-timers.output'
import type { OutputSubTimer } from '@/features/rundown/cue/sub-timers/sub-timers.output'
import { useCueSnapshot } from '@/features/rundown/data/cues.store'

import { RundownOutputTextFitWrapper } from './RundownOutputTextFitWrapper'

// Sub timers set to show on output, in place of the cue countdown. Stopped
// timers are dimmed; overtime shows in red.

type SlotProps = {
  /** `stack` for the big timer area, `row` for the header's single line. */
  layout: 'stack' | 'row'
  heightVh: number
}

const NO_SUB_TIMERS: SubTimer[] = []

const activeCueIdAtom = selectAtom(
  runnerAtom,
  (runner) => runner?.timesnap?.cueId ?? null,
)

/**
 * A countdown slot showing the active cue's sub timers set to show on output
 * that are running or paused, so the output stays about the cue on air and
 * falls back to its countdown while they are idle or reset.
 *
 * Its own component so their 250ms tick re-renders this slot, not the layout.
 */
export function RundownOutputActiveCueTimer(props: SlotProps) {
  const cueId = useAtomValue(activeCueIdAtom)
  const subTimers = useCueSnapshot(cueId ?? '')?.subTimers ?? NO_SUB_TIMERS
  return (
    <CountdownSlot
      timers={useCueOutputSubTimers(cueId ?? '', subTimers).filter(
        (timer) => timer.view.started,
      )}
      {...props}
    />
  )
}

function CountdownSlot({
  timers,
  layout,
  heightVh,
}: SlotProps & { timers: OutputSubTimer[] }) {
  const moment = useAtomValue(momentAtom)

  if (timers.length > 0) {
    return layout === 'stack' ? (
      <RundownOutputSubTimerStack
        timers={timers}
        heightVh={heightVh}
      />
    ) : (
      <RundownOutputSubTimerRow
        timers={timers}
        heightVh={heightVh}
      />
    )
  }

  return (
    <RundownOutputTextFitWrapper
      height={`${heightVh}vh`}
      className="font-mono font-semibold"
    >
      <div className="whitespace-nowrap">
        <CueElapsed
          moment={moment}
          className="bg-transparent!"
          overtimeClassName=""
        />
      </div>
    </RundownOutputTextFitWrapper>
  )
}

function TimerLabel({ timer }: { timer: OutputSubTimer }) {
  return (
    <span
      className={cn(
        'px-2',
        timer.view.over
          ? 'text-red-500'
          : timer.view.running
            ? 'text-white'
            : 'text-white/60',
      )}
    >
      {timer.view.label}
    </span>
  )
}

/**
 * Stacked, sharing the height of the main timer area evenly. Names sit above
 * their time when any timer has one, so the times line up.
 */
function RundownOutputSubTimerStack({
  timers,
  heightVh,
}: {
  timers: OutputSubTimer[]
  heightVh: number
}) {
  const each = heightVh / timers.length
  const withNames = timers.some((t) => t.name)
  const nameVh = withNames ? Math.min(each * 0.2, 6) : 0

  return (
    <div className="flex flex-col">
      {timers.map((timer) => (
        <div
          key={timer.id}
          style={{ height: `${each}vh` }}
        >
          {withNames && (
            // A fixed size rather than fitted text: every name matches,
            // whatever its length
            <div
              className="truncate px-5 text-center opacity-60"
              style={{
                height: `${nameVh}vh`,
                lineHeight: `${nameVh}vh`,
                fontSize: `${nameVh * 0.8}vh`,
              }}
            >
              {timer.name}
            </div>
          )}
          <RundownOutputTextFitWrapper
            height={`${each - nameVh}vh`}
            className="font-mono font-semibold"
          >
            <TimerLabel timer={timer} />
          </RundownOutputTextFitWrapper>
        </div>
      ))}
    </div>
  )
}

/** In a row, for the header's single-line countdown slot. */
function RundownOutputSubTimerRow({
  timers,
  heightVh,
}: {
  timers: OutputSubTimer[]
  heightVh: number
}) {
  return (
    <div className="flex items-center gap-6">
      {timers.map((timer) => (
        <RundownOutputTextFitWrapper
          key={timer.id}
          height={`${heightVh}vh`}
        >
          <div className="whitespace-nowrap font-semibold">
            {timer.name && (
              <span className="mr-2 font-normal opacity-60">{timer.name}</span>
            )}
            <span className="font-mono">
              <TimerLabel timer={timer} />
            </span>
          </div>
        </RundownOutputTextFitWrapper>
      ))}
    </div>
  )
}

/**
 * A list output row's sub timers, stacked under its duration. Overtime keeps a
 * dark chip here so red stays readable on the active row's red background.
 */
export function RundownOutputCueSubTimers({
  cueId,
  subTimers,
}: {
  cueId: string
  subTimers: readonly SubTimer[]
}) {
  const timers = useCueOutputSubTimers(cueId, subTimers)

  return (
    <div className="flex flex-col gap-1">
      {timers.map((timer) => (
        <div
          key={timer.id}
          className="min-w-0 leading-tight"
        >
          {timer.name && (
            <p className="truncate text-sm opacity-60">{timer.name}</p>
          )}
          <p
            className={cn(
              'w-max font-mono',
              timer.view.over
                ? 'rounded-sm bg-gray-900 px-1 text-red-500'
                : timer.view.running
                  ? 'text-white'
                  : 'text-white/60',
            )}
          >
            {timer.view.label}
          </p>
        </div>
      ))}
    </div>
  )
}
